import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signInAnonymously, signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { auth } from '../firebase';
import type { User } from '../types';
import { toast } from '../components/ui/Toaster';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  loginAsGuest: () => Promise<void>;
  updateUserName: (name: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        let displayName = firebaseUser.displayName || (firebaseUser.isAnonymous ? 'Guest User' : 'User');
        let photoURL = firebaseUser.photoURL;

        try {
          const { userService } = await import('../services/userService');
          let profile = await userService.getUserProfile(firebaseUser.uid);
          if (!profile) {
            // Document doesn't exist yet, auto-create it immediately with current details
            const defaultProfile = {
              uid: firebaseUser.uid,
              displayName,
              photoURL: photoURL || null,
              email: firebaseUser.email,
            };
            await userService.updateUserProfile(firebaseUser.uid, defaultProfile);
            profile = defaultProfile as any;
          }
          
          if (profile && profile.displayName) {
            displayName = profile.displayName;
          }
          if (profile && profile.photoURL) {
            photoURL = profile.photoURL;
          }
        } catch (error) {
          console.error("Error loading user profile from Firestore:", error);
        }

        const formattedUser: User = {
            uid: firebaseUser.uid,
            displayName,
            email: firebaseUser.email,
            photoURL,
        };
        setUser(formattedUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const updateUserName = async (name: string) => {
    if (auth.currentUser) {
        try {
            const { updateProfile } = await import('firebase/auth');
            await updateProfile(auth.currentUser, { displayName: name });
            const { userService } = await import('../services/userService');
            await userService.updateUserProfile(auth.currentUser.uid, { displayName: name });
            setUser(prev => prev ? { ...prev, displayName: name } : null);
        } catch (error) {
            console.error("Error updating profile name:", error);
            throw error;
        }
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      // Set persistence to LOCAL
      await setPersistence(auth, browserLocalPersistence);
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      console.error("Error signing in with Google: ", error);
      let errorMessage = "Failed to sign in.";
      if (error.code === 'auth/operation-not-supported-in-this-environment') {
          errorMessage = "Google Sign-In is not supported in this environment. Try opening in a new tab or use 'Continue as Guest'.";
      } else if (error.code === 'auth/popup-blocked') {
          errorMessage = "Popup was blocked. Please allow popups for this site.";
      } else if (error.code === 'auth/unauthorized-domain') {
          errorMessage = "This domain is not authorized for sign-in. The Firebase setup might still be propagating.";
      }
      toast(errorMessage, 'error');
    }
  };

  const loginAsGuest = async () => {
    try {
        await signInAnonymously(auth);
        toast("Signed in as Guest", "success");
    } catch (error: any) {
        console.error("Guest login failed:", error);
        toast("Failed to sign in as guest.", "error");
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      toast("Logged out.", "success");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signInWithGoogle, logout, loginAsGuest, updateUserName }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
